import React, { useContext, useRef, useState, useMemo, useEffect } from 'react';
import { ThemeContext } from 'styled-components';
import { useMediaQuery } from '@nebula/hooks';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import { RadioButton, RadioButtonType, RadioButtonState } from '@nebula/radio-button';
import { TitleMedium500, TitleLarge500, BodyMedium400, BodyLarge400 } from '@nebula/global-styles';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { getEnv } from '../../Context/env';
import { trackSwitchPaymentMethodSelected, trackCardOnFileSplit } from '../../../helpers/switch/analytics';
import InViewComponent from '../../InView';
import { FamilyName, PaymentCard, PaymentMethodTypes } from '../constants.ts';
import { PaymentContext } from '../../PaymentOrchestrator/PaymentContextProvider';
import useExistingPaymentMethod from '../hooks/useExistingPaymentMethod';
import PaymentCardForm from '../PaymentCardForm';
import { SmallCardIcon } from '../PaymentCardForm/Icons';
import ApplePay from './ApplePay';
import GooglePay from './GooglePay';
import {
  PaymentDivider,
  SubTitle,
  PaymentMethod,
  PaymentMethodTitle,
  Spinner,
  ConfirmButton,
  ApplePayOption,
  GooglePayOption,
  PaymentSection,
  PaymentDescription,
} from './index.styles';
import usePaymentMethods from '../../PaymentOrchestrator/hooks/usePaymentMethods';
import useSplits from '../../../hooks/useSplits';
import { products, splitsList } from '../../../constants';
import ExistingPaymentCvvForm from '../ExistingPaymentCvvForm';
import useTrackUpsellStage from '../../PaymentOrchestrator/hooks/useTrackUpsellStage';
import { PAYMENT_LOG_STEPS } from '../../../constants/paymentLogSteps';
import useBillingChangeInfo from '../hooks/useBillingChangeInfo';

const WALLET_PAYMENT_TYPES = new Set([
  PaymentMethodTypes.ApplePay,
  PaymentMethodTypes.NewApplePay,
  PaymentMethodTypes.GooglePay,
  PaymentMethodTypes.NewGpay,
]);

const isProductSwitchMovement = (currentFamily, targetFamily) =>
  currentFamily !== targetFamily &&
  [products.identity, products.credit_expert].includes(currentFamily) &&
  [products.identity, products.credit_expert].includes(targetFamily);

const PaymentMethodEntry = ({ disabled, onSubmit, onSuccess, onFailure, onExistingPaymentMethod }) => {
  const theme = useContext(ThemeContext);
  const { REACT_APP_RECURLY_PUBLIC_KEY: key } = getEnv();
  const formRef = useRef(null);
  const cvvFormRef = useRef(null);
  const isSubmitting = useRef(false);
  const paymentContext = useContext(PaymentContext);
  const {
    resolvedIsTrial,
    resolvedUpgradeOffer,
    paymentErrors,
    isBillingPeriodChange,
    isFlexFlow,
    resolvedCurrentSubscription,
    isRestart,
  } = paymentContext;
  const reduxCurrentSubscription = useSelector(state => state.subscriptions?.current);
  const currentSubscription = isFlexFlow ? resolvedCurrentSubscription : reduxCurrentSubscription;

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(PaymentCard.Existing);

  const splitReady = useSelector(state => state.split?.fetched);
  const splits = useSplits([splitsList.disableSavedPaymentMethod, splitsList.preventDuplicateBillingCall]);
  const disableSavedPaymentMethod = splits[splitsList.disableSavedPaymentMethod];
  const isGuardEnabled = splits[splitsList.preventDuplicateBillingCall];
  const trackUpsellStage = useTrackUpsellStage();

  const { existingPaymentType, existingPaymentDetails } = useExistingPaymentMethod();
  const existingCardAvailable = !disableSavedPaymentMethod && existingPaymentType === PaymentMethodTypes.Card;
  const { creditCardType, cardNumber, cardExpiryDate } = existingPaymentDetails;
  const billingInfoId = useSelector(state => state.billing?.card?.billingProviderPaymentMethodId);
  const isCypress = typeof window !== 'undefined' && window.Cypress;
  const isTabletPortrait = useMediaQuery(theme.media.tabletPortraitMin);
  const PaymentDetailsText = isTabletPortrait ? TitleLarge500 : TitleMedium500;

  const { billingStartDate } = useBillingChangeInfo({
    upgradeOffer: resolvedUpgradeOffer,
    isTrial: resolvedIsTrial,
  });

  const formattedStartDate = billingStartDate.format('DD MMMM YYYY');
  const isCreditExpertToIdentityMovement =
    currentSubscription?.family === products.credit_expert && resolvedUpgradeOffer?.family === products.identity;

  useEffect(() => {
    if (!splitReady) return;

    trackCardOnFileSplit(existingCardAvailable);
  }, [splitReady, existingCardAvailable]);

  useEffect(() => {
    if (isGuardEnabled && paymentErrors?.length > 0) {
      trackUpsellStage(PAYMENT_LOG_STEPS.DUPLICATE_PAYMENT_GUARD_RESET_BILLING_FAILURE);
      isSubmitting.current = false;
    }
  }, [paymentErrors, isGuardEnabled]);

  // Build recurlyConfig with isolated risk config only when existingCardAvailable is true
  const recurlyConfig = useMemo(() => {
    const config = { publicKey: key };

    // Only add 3DS risk config for users in split in this flow
    if (!disableSavedPaymentMethod && !isCypress) {
      config.risk = {
        threeDSecure: {
          preflightDeviceDataCollector: {
            enabled: true,
            ...(billingInfoId ? { billingInfoId } : {}),
          },
        },
      };
    }

    return config;
  }, [key, disableSavedPaymentMethod, isCypress, billingInfoId]);

  const availablePaymentMethods = usePaymentMethods();
  const showApplePay = availablePaymentMethods.includes(PaymentMethodTypes.ApplePay);
  const showGooglePay = availablePaymentMethods.includes(PaymentMethodTypes.GooglePay);

  const hasValidExistingMethodSelected = selectedMethod =>
    existingCardAvailable && selectedMethod === PaymentCard.Existing;

  const [isFormValid, setIsFormValid] = useState(hasValidExistingMethodSelected(selectedPaymentMethod));
  const handleFormValidityChange = isValid => setIsFormValid(isValid);

  const handlePaymentMethodSelection = value => {
    trackSwitchPaymentMethodSelected(value);
    setSelectedPaymentMethod(value);

    if (hasValidExistingMethodSelected(value)) {
      handleFormValidityChange(true);
    }
  };

  const customer = useSelector(state => state.auth?.currentUser);
  const address = customer?.address?.[0];

  const handleSubmit = () => {
    onSubmit?.();
  };

  const handleSuccess = (paymentMethodToken, paymentType) => {
    if (isGuardEnabled && isSubmitting.current) {
      trackUpsellStage(PAYMENT_LOG_STEPS.DUPLICATE_PAYMENT_BLOCKED);
      return;
    }

    if (isGuardEnabled) {
      trackUpsellStage(PAYMENT_LOG_STEPS.DUPLICATE_PAYMENT_GUARD_SET);
      isSubmitting.current = true;
    }

    // If the payment type is a wallet payment, we can call onSuccess directly without checking for existing payment method (i.e. set the isAddingPaymentMethod to true in the payload)
    if (WALLET_PAYMENT_TYPES.has(paymentType)) {
      onSuccess?.(paymentMethodToken, paymentType);
      return;
    }

    if (hasValidExistingMethodSelected(selectedPaymentMethod)) {
      onExistingPaymentMethod?.(paymentMethodToken?.id, paymentType);
    } else {
      onSuccess?.(paymentMethodToken, paymentType);
    }
  };

  const handleFailure = (paymentMethodError, paymentType) => {
    if (isGuardEnabled) {
      trackUpsellStage(PAYMENT_LOG_STEPS.DUPLICATE_PAYMENT_GUARD_RESET_TOKENISATION_FAILURE);
      isSubmitting.current = false;
    }
    onFailure?.(paymentMethodError, paymentType);
  };

  const handleConfirm = () => {
    if (hasValidExistingMethodSelected(selectedPaymentMethod)) {
      if (cvvFormRef.current) {
        cvvFormRef.current.requestSubmit();
      }
    } else if (formRef.current) {
      if (formRef.current.requestSubmit) {
        formRef.current.requestSubmit();
      } else {
        // compatibility fallback for older browsers like Safari<16 without support for requestSubmit
        formRef.current.dispatchEvent(new window.Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  };

  const getConfirmButtonText = () => {
    if (isBillingPeriodChange) {
      return 'Update billing period';
    }
    if (resolvedIsTrial) {
      return 'Start my free trial';
    }
    if (isRestart) {
      return `Restart ${FamilyName[resolvedUpgradeOffer.family]}`;
    }
    const isProductSwitch = isProductSwitchMovement(currentSubscription?.family, resolvedUpgradeOffer.family);
    return `${isProductSwitch ? 'Switch' : 'Upgrade'} to ${FamilyName[resolvedUpgradeOffer.family]}`;
  };

  const confirmButtonText = getConfirmButtonText();

  return (
    <RecurlyProvider {...recurlyConfig}>
      <Elements>
        <React.Fragment>
          <PaymentDetailsText as="h2">Payment details</PaymentDetailsText>
          <BodyLarge400>{resolvedIsTrial && 'No payment is taken during your trial'}</BodyLarge400>
          {isCreditExpertToIdentityMovement && !resolvedIsTrial && (
            <PaymentDescription>
              Payment will be taken when your new subscription starts on {formattedStartDate}.
            </PaymentDescription>
          )}
          {isRestart && (
            <PaymentDescription>
              We’ll save your payment details for your future subscription payments.
            </PaymentDescription>
          )}
        </React.Fragment>
        <PaymentSection>
          {showApplePay && (
            <ApplePayOption disabled={disabled}>
              <ApplePay onSuccess={handleSuccess} onError={handleFailure} />
              <PaymentDivider>or</PaymentDivider>
            </ApplePayOption>
          )}
          {showGooglePay && (
            <GooglePayOption disabled={disabled}>
              <GooglePay onSuccess={handleSuccess} onError={handleFailure} />
              <PaymentDivider>or</PaymentDivider>
            </GooglePayOption>
          )}
        </PaymentSection>
        {splitReady && (
          <InViewComponent label="switch_payment_form" data={{ available_payment_methods: availablePaymentMethods }}>
            <SubTitle as="h3">Pay by card</SubTitle>
          </InViewComponent>
        )}
        {existingCardAvailable ? (
          <React.Fragment>
            <PaymentMethod>
              <RadioButton
                id={PaymentCard.Existing}
                label={
                  <PaymentMethodTitle data-hook="fs-mask-element">
                    <SmallCardIcon creditCardType={creditCardType} /> •••• {cardNumber} | Expires {cardExpiryDate}
                  </PaymentMethodTitle>
                }
                value={PaymentCard.Existing}
                index={1}
                radioType={RadioButtonType.Standard}
                radioState={RadioButtonState.Standard}
                width="100%"
                isChecked={selectedPaymentMethod === PaymentCard.Existing}
                isDisabled={false}
                onRadioButtonSelected={() => handlePaymentMethodSelection(PaymentCard.Existing)}
              />
              {selectedPaymentMethod === PaymentCard.Existing && (
                <ExistingPaymentCvvForm
                  innerRef={cvvFormRef}
                  onSubmitAttempt={handleSubmit}
                  onSuccess={handleSuccess}
                  onFailure={handleFailure}
                  onFormValidityChange={handleFormValidityChange}
                />
              )}
            </PaymentMethod>
            <PaymentMethod>
              <RadioButton
                id={PaymentCard.New}
                label={<PaymentMethodTitle>Use a different card</PaymentMethodTitle>}
                value={PaymentCard.New}
                index={2}
                radioType={RadioButtonType.Standard}
                radioState={RadioButtonState.Standard}
                width="100%"
                isChecked={selectedPaymentMethod === PaymentCard.New}
                isDisabled={false}
                onRadioButtonSelected={() => handlePaymentMethodSelection(PaymentCard.New)}
                data-automation-test-element="use-different-card"
              />
              {selectedPaymentMethod === PaymentCard.New && (
                <PaymentCardForm
                  innerRef={formRef}
                  customer={customer}
                  address={address}
                  onSubmitAttempt={handleSubmit}
                  onSuccess={handleSuccess}
                  onFailure={handleFailure}
                  onFormValidityChange={handleFormValidityChange}
                />
              )}
            </PaymentMethod>
          </React.Fragment>
        ) : (
          <PaymentMethod>
            <PaymentCardForm
              innerRef={formRef}
              customer={customer}
              address={address}
              onSubmitAttempt={handleSubmit}
              onSuccess={handleSuccess}
              onFailure={handleFailure}
              onFormValidityChange={handleFormValidityChange}
            />
          </PaymentMethod>
        )}
        <ConfirmButton
          data-automation-test-element="confirmation-button"
          disabled={!isFormValid || disabled}
          label={disabled ? <Spinner /> : confirmButtonText}
          buttonKind="primaryMagenta"
          onClick={handleConfirm}
        />
        <BodyMedium400>Report data sourced from: UK lenders and local to you public sources</BodyMedium400>
      </Elements>
    </RecurlyProvider>
  );
};

PaymentMethodEntry.propTypes = {
  disabled: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onFailure: PropTypes.func.isRequired,
  onExistingPaymentMethod: PropTypes.func.isRequired,
};

export default PaymentMethodEntry;
